import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    FileText, ArrowUpRight, ArrowDownLeft, Download, Building2, 
    CalendarDays, Euro, TrendingUp, TrendingDown, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '@/lib/api';

export default function AccountantDashboard() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [period, setPeriod] = useState('month');
    const [companyName, setCompanyName] = useState('');

    useEffect(() => {
        loadDashboard();
        
        // Get company name from token
        const token = localStorage.getItem('accountant_token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                // This would need to be stored during login
            } catch (e) {
                console.error('Error parsing token:', e);
            }
        }
        
        const storedCompany = localStorage.getItem('accountant_company');
        if (storedCompany) {
            setCompanyName(storedCompany);
        }
    }, [period]);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accountant_token');
            if (!token) {
                window.location.href = '/accountant-login';
                return;
            }
            
            const res = await api.get(`/accountant/dashboard?period=${period}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(res.data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
            if (error.response?.status === 401) {
                localStorage.removeItem('accountant_token');
                localStorage.removeItem('accountant_company');
                window.location.href = '/accountant-login';
            } else {
                toast.error('Erreur lors du chargement des données');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('accountant_token');
        localStorage.removeItem('accountant_company');
        window.location.href = '/accountant-login';
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr });
    };

    const periodLabels = {
        month: 'Ce mois',
        quarter: 'Ce trimestre',
        year: 'Cette année'
    };

    const handleExportCsv = async () => {
        try {
            const token = localStorage.getItem('accountant_token');
            const response = await api.get(`/accountant/export-csv?period=${period}`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `export_factures_${period}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Export CSV téléchargé');
        } catch (error) {
            toast.error('Erreur lors de l\'export CSV');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 p-8">
                <div className="max-w-7xl mx-auto space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 px-8 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Building2 className="w-8 h-8 text-indigo-600" />
                        <div>
                            <h1 className="text-xl font-semibold text-slate-900">
                                Espace Comptable
                            </h1>
                            <p className="text-sm text-slate-500">
                                {companyName || 'Vue lecture seule'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Select value={period} onValueChange={setPeriod}>
                            <SelectTrigger className="w-40">
                                <CalendarDays className="w-4 h-4 mr-2" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="month">Ce mois</SelectItem>
                                <SelectItem value="quarter">Ce trimestre</SelectItem>
                                <SelectItem value="year">Cette année</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={handleExportCsv}>
                            <Download className="w-4 h-4 mr-2" />
                            Export CSV
                        </Button>
                        <Button variant="outline" onClick={handleLogout}>
                            Déconnexion
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto p-8 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Sorties */}
                    <Card className="bg-red-50 border-red-200">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-red-700">
                                <ArrowUpRight className="w-5 h-5" />
                                Sorties (Factures émises)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-red-600">Total HT</span>
                                    <span className="font-semibold text-red-700">
                                        {formatCurrency(data?.outgoing?.total_ht)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-red-600">Total TTC</span>
                                    <span className="font-bold text-red-800 text-lg">
                                        {formatCurrency(data?.outgoing?.total_ttc)}
                                    </span>
                                </div>
                                <div className="text-xs text-red-500 mt-2">
                                    {data?.outgoing?.count || 0} factures
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Entrées */}
                    <Card className="bg-green-50 border-green-200">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-green-700">
                                <ArrowDownLeft className="w-5 h-5" />
                                Entrées (Factures reçues)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-green-600">Total HT</span>
                                    <span className="font-semibold text-green-700">
                                        {formatCurrency(data?.incoming?.total_ht)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-green-600">Total TTC</span>
                                    <span className="font-bold text-green-800 text-lg">
                                        {formatCurrency(data?.incoming?.total_ttc)}
                                    </span>
                                </div>
                                <div className="text-xs text-green-500 mt-2">
                                    {data?.incoming?.count || 0} factures
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Solde */}
                    <Card className={data?.balance_ttc >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}>
                        <CardHeader className="pb-2">
                            <CardTitle className={`flex items-center gap-2 ${data?.balance_ttc >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                                {data?.balance_ttc >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                                Solde (Sorties - Entrées)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className={`text-sm ${data?.balance_ttc >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>Solde HT</span>
                                    <span className={`font-semibold ${data?.balance_ttc >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>
                                        {formatCurrency(data?.balance_ht)}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className={`text-sm ${data?.balance_ttc >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>Solde TTC</span>
                                    <span className={`font-bold text-lg ${data?.balance_ttc >= 0 ? 'text-blue-800' : 'text-amber-800'}`}>
                                        {formatCurrency(data?.balance_ttc)}
                                    </span>
                                </div>
                                <div className={`text-xs mt-2 ${data?.balance_ttc >= 0 ? 'text-blue-500' : 'text-amber-500'}`}>
                                    {periodLabels[period]}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Invoice Tables */}
                <Tabs defaultValue="outgoing" className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="outgoing" className="flex items-center gap-2">
                            <ArrowUpRight className="w-4 h-4" />
                            Factures Émises ({data?.outgoing?.count || 0})
                        </TabsTrigger>
                        <TabsTrigger value="incoming" className="flex items-center gap-2">
                            <ArrowDownLeft className="w-4 h-4" />
                            Factures Reçues ({data?.incoming?.count || 0})
                        </TabsTrigger>
                    </TabsList>

                    {/* Outgoing Invoices */}
                    <TabsContent value="outgoing">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ArrowUpRight className="w-5 h-5 text-red-500" />
                                    Factures Émises
                                </CardTitle>
                                <CardDescription>
                                    Factures que vous avez émises à vos clients / donneurs d'ordre
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {data?.outgoing?.invoices?.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>Aucune facture émise pour cette période</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>N° Facture</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Client / Payeur</TableHead>
                                                <TableHead>Référence</TableHead>
                                                <TableHead className="text-right">HT</TableHead>
                                                <TableHead className="text-right">TVA</TableHead>
                                                <TableHead className="text-right">TTC</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data?.outgoing?.invoices?.map((invoice) => (
                                                <TableRow key={invoice.id}>
                                                    <TableCell className="font-mono font-medium">
                                                        {invoice.invoice_number}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(invoice.issued_at || invoice.created_at)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {invoice.payer_name}
                                                    </TableCell>
                                                    <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                                                        {invoice.prospect_refs || '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatCurrency(invoice.total_ht)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-slate-500">
                                                        {formatCurrency(invoice.tva_amount)}
                                                    </TableCell>
                                                    <TableCell className="text-right font-semibold">
                                                        {formatCurrency(invoice.total_ttc)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={
                                                            invoice.status === 'payee' ? 'bg-green-100 text-green-700' :
                                                            invoice.status === 'emise' ? 'bg-indigo-100 text-indigo-700' :
                                                            'bg-slate-100 text-slate-700'
                                                        }>
                                                            {invoice.status === 'payee' ? 'Payée' :
                                                             invoice.status === 'emise' ? 'Émise' : invoice.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button size="sm" variant="ghost">
                                                            <Download className="w-4 h-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Incoming Invoices */}
                    <TabsContent value="incoming">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <ArrowDownLeft className="w-5 h-5 text-green-500" />
                                    Factures Reçues
                                </CardTitle>
                                <CardDescription>
                                    Factures que vous avez reçues de vos fournisseurs / prestataires
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {data?.incoming?.invoices?.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>Aucune facture reçue pour cette période</p>
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>N° Facture</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Fournisseur / Émetteur</TableHead>
                                                <TableHead>Référence</TableHead>
                                                <TableHead className="text-right">HT</TableHead>
                                                <TableHead className="text-right">TVA</TableHead>
                                                <TableHead className="text-right">TTC</TableHead>
                                                <TableHead>Statut</TableHead>
                                                <TableHead></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {data?.incoming?.invoices?.map((invoice) => (
                                                <TableRow key={invoice.id}>
                                                    <TableCell className="font-mono font-medium">
                                                        {invoice.invoice_number}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDate(invoice.issued_at || invoice.created_at)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {invoice.issuer_name}
                                                    </TableCell>
                                                    <TableCell className="text-sm text-slate-500 max-w-xs truncate">
                                                        {invoice.prospect_refs || '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatCurrency(invoice.total_ht)}
                                                    </TableCell>
                                                    <TableCell className="text-right text-slate-500">
                                                        {formatCurrency(invoice.tva_amount)}
                                                    </TableCell>
                                                    <TableCell className="text-right font-semibold">
                                                        {formatCurrency(invoice.total_ttc)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge className={
                                                            invoice.status === 'payee' ? 'bg-green-100 text-green-700' :
                                                            invoice.status === 'emise' ? 'bg-indigo-100 text-indigo-700' :
                                                            'bg-slate-100 text-slate-700'
                                                        }>
                                                            {invoice.status === 'payee' ? 'Payée' :
                                                             invoice.status === 'emise' ? 'Émise' : invoice.status}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button size="sm" variant="ghost">
                                                            <Download className="w-4 h-4" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                {/* Footer Info */}
                <div className="text-center text-xs text-slate-400 pt-8">
                    <p>Accès en lecture seule • Les données sont synchronisées automatiquement</p>
                    <p>Pour toute question, contactez le titulaire du compte</p>
                </div>
            </main>
        </div>
    );
}
